import { useState } from "react";
import { useFAQSections, FAQSection, FAQ } from "@/hooks/use-faq-sections";
import { useDeleteFAQSection } from "@/hooks/use-faq-sections";
import { useDeleteFAQ } from "@/hooks/use-faqs";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Edit, Trash2, HelpCircle, Plus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FAQSectionModal } from "./FAQSectionModal";
import { FAQModal } from "./FAQModal";

export function FAQList() {
  const { data: sections, isLoading, error } = useFAQSections();
  const { isSystemAdmin } = useSimpleRole();
  const deleteSectionMutation = useDeleteFAQSection();
  const deleteFAQMutation = useDeleteFAQ();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [editingSection, setEditingSection] = useState<FAQSection | null>(null);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [deletingSection, setDeletingSection] = useState<FAQSection | null>(null);
  const [deletingFAQ, setDeletingFAQ] = useState<FAQ | null>(null);
  const [addingToSection, setAddingToSection] = useState<string | null>(null);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleDeleteSection = () => {
    if (deletingSection) {
      deleteSectionMutation.mutate(deletingSection.id, {
        onSuccess: () => setDeletingSection(null),
      });
    }
  };

  const handleDeleteFAQ = () => {
    if (deletingFAQ) {
      deleteFAQMutation.mutate(deletingFAQ.id, {
        onSuccess: () => setDeletingFAQ(null),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-6 w-48 mb-4" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Failed to load FAQs. Please try again later.
        </CardContent>
      </Card>
    );
  }

  if (!sections || sections.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No FAQs yet</h3>
          <p className="text-muted-foreground">
            {isSystemAdmin
              ? "Get started by adding FAQ sections and questions."
              : "FAQs will appear here once they are added."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const isOpen = openSections[section.id] ?? true;

        return (
          <Card key={section.id} className="overflow-hidden">
            <Collapsible open={isOpen} onOpenChange={() => toggleSection(section.id)}>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {isOpen ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div>
                      <h3 className="font-semibold text-lg">{section.name}</h3>
                      {section.description && (
                        <p className="text-sm text-muted-foreground">{section.description}</p>
                      )}
                    </div>
                  </div>
                  {isSystemAdmin && (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingSection(section)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeletingSection(section)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4">
                  {section.faqs && section.faqs.length > 0 ? (
                    <Accordion type="multiple" className="w-full">
                      {section.faqs.map((faq) => (
                        <AccordionItem key={faq.id} value={faq.id} className="border-b last:border-0">
                          <AccordionTrigger className="text-left hover:no-underline py-3 justify-start">
                            <span className="font-medium text-left">{faq.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="pb-4">
                            <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
                              {faq.answer}
                            </div>
                            {isSystemAdmin && (
                              <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEditingFAQ(faq)}
                                >
                                  <Edit className="h-3 w-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDeletingFAQ(faq)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <p className="text-sm text-muted-foreground py-2 pl-8">
                      No questions in this section yet.
                    </p>
                  )}
                  {isSystemAdmin && (
                    <div className="mt-4 pl-8">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddingToSection(section.id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Question
                      </Button>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}

      {/* Edit Section Modal */}
      {editingSection && (
        <FAQSectionModal
          open={!!editingSection}
          onOpenChange={(open) => !open && setEditingSection(null)}
          section={editingSection}
        />
      )}

      {/* Edit FAQ Modal */}
      {editingFAQ && (
        <FAQModal
          open={!!editingFAQ}
          onOpenChange={(open) => !open && setEditingFAQ(null)}
          faq={editingFAQ}
        />
      )}

      {/* Delete Section Confirmation */}
      <AlertDialog open={!!deletingSection} onOpenChange={(open) => !open && setDeletingSection(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ Section</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingSection?.name}"? This will also delete all FAQs within this section. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSection}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete FAQ Confirmation */}
      <AlertDialog open={!!deletingFAQ} onOpenChange={(open) => !open && setDeletingFAQ(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this FAQ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFAQ}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add FAQ Modal with pre-selected section */}
      <FAQModal
        open={!!addingToSection}
        onOpenChange={(open) => !open && setAddingToSection(null)}
        defaultSectionId={addingToSection || undefined}
      />
    </div>
  );
}
